import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { client } from '@mopla/ticketshop-api';

/** Thrown by `sendInvoices` for any non-204 response. */
export interface ITicketInvoiceError {
	/** HTTP status (409 no paid invoice / no e-mail, 429 rate-limited, 502, 503). */
	status: number;
	/** Seconds from the `Retry-After` header (429/503), when present. */
	retryAfterSeconds?: number;
}

/**
 * POST /api/v1/me/tickets/{purchaseId}/invoices — e-mails every paid invoice of
 * the purchase to the passenger (one e-mail + PDF per paid installment). No body.
 * `Accept-Language` drives the PDF/e-mail language (BE falls back to German).
 * A 204 resolves; every other status throws `ITicketInvoiceError` so the caller
 * can show the matching message. The bearer token is added by the ticketshop
 * client interceptor.
 */
export const useSendTicketInvoices = () => {
	const { i18n } = useTranslation();

	const sendInvoices = useCallback(
		async (purchaseId: string): Promise<void> => {
			const { response } = await client.post({
				url: `/api/v1/me/tickets/${purchaseId}/invoices`,
				headers: { 'Accept-Language': i18n.language },
				throwOnError: false,
			});

			if (response.ok) {
				return;
			}

			const retryAfter = response.headers.get('Retry-After');
			const error: ITicketInvoiceError = {
				status: response.status,
				retryAfterSeconds: retryAfter ? Number(retryAfter) : undefined,
			};
			throw error;
		},
		[i18n.language]
	);

	return { sendInvoices };
};
